import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TeButton,
  TeButtonOutline,
  TeCallout,
  TeContainer,
  TeContainerSlim,
  TeDivider,
  TeIcon,
  TeLabel,
  TeSubmit,
  TeText,
  TeGrid,
  TeGridItem,
} from '@topcon/tuicl-react';
import { PlaceholderContainer, useNotification } from '@topcon/topcon-utils';
import { Statuses } from '@topcon/tuicl/dist/common/component-types';
import { generateRandomId } from '@topcon/tuicl/dist/common/utils';
import styled from 'styled-components';

import { Section } from '../../utils/styling';
import Header from '../../components/Header';
import AdjustLogoModal from './AdjustLogoModal';
import {
  useDeleteBusinessPartnerIcon,
  useUpdateBusinessPartnerIcon,
  useUpdateCurrentBusinessPartnerPM,
} from './businessPartner.mutations';
import { useCancelChangeOwnershipRequest } from '../RequestChangeOwnership/changeOwnership.mutations';
import { useMarkMigrationDataAsCorrect } from '../../hooks/queries/migration.mutations';
import {
  invalidateCurrentBusinessPartnerChangePM,
  useCurrentBusinessPartner,
  useCurrentBusinessPartnerChangePM,
  useCurrentBusinessPartnerPM,
} from './businessPartner.queries';
import { useCurrentChangeOwnershipRequest } from '../RequestChangeOwnership/changeOwnership.queries';
import { useCurrentBusinessPartnerWithIdentityInfo } from '../../hooks/queries/appSession.queries';
import FileDropContainer from '../../components/FileDropContainer';
import UploadButton from '../../components/UploadButton';
import TransferOwnerModal from './TransferOwnerModal';
import { BusinessPartnerBody, BusinessPartnerChangeBody } from '../../api/generated/pm';
import { zBusinessPartnerChangeBody } from '../../api/generated/pm/zod.gen';
import SchemaValidatedForm from '../../components/SchemaValidatedForm';
import { useIsDivision } from '../../app/routes/utils';
import { CountryDropdown } from '../../components/CountryDropdown';
import { DEFAULT_COUNTRY } from '../../app/constants';
import { copyToClipboard, extractTopconCustomerNumber } from '../../utils/utils';
import { formatOrganizationNumber } from '../../utils/organizationNumberFormatter';
import CompanyIcon from '../../components/Company/CompanyIcon';
import featureFlags from '../../utils/featureFlags';
import { MigrationCallout } from '../../components/Callout/MigrationCallout';
import { BREAKPOINTS } from '../../utils/breakpoints';
import { useErrorDrawer } from '../../providers/ErrorDrawerContext/error-drawer.context';

const ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/png'];
const BTN_ACCEPT = ACCEPTED_FILE_TYPES.join(',');

const FormSchema = zBusinessPartnerChangeBody.shape.companyAddress.extend({
  name: zBusinessPartnerChangeBody.shape.name.min(1),
});

function getFormDataFromBP(
  businessPartner: BusinessPartnerBody | undefined,
  change: BusinessPartnerChangeBody | undefined
) {
  const addressLineTwo =
    businessPartner?.companyAddress.addressLineTwo ?? change?.companyAddress.addressLineTwo;
  return {
    name: change?.name ?? businessPartner?.name ?? '',
    companyAddress: {
      addressLineOne: '',
      city: '',
      country: 'US',
      stateProvinceRegion: '',
      zipPostalCode: '',
      ...businessPartner?.companyAddress,
      ...change?.companyAddress,
      addressLineTwo: addressLineTwo == null ? undefined : addressLineTwo,
    },
  } satisfies BusinessPartnerChangeBody;
}

const GeneralInformation = () => {
  const { t } = useTranslation('settings');
  const { t: commonT } = useTranslation('common');
  const notificationService = useNotification();
  const { showError } = useErrorDrawer();
  const {
    data: currentBusinessPartner,
    isPending: isCurrentBusinessPartnerPending,
    refetch: refetchCurrentBusinessPartner,
  } = useCurrentBusinessPartnerPM();

  const { data: currentBPWithIdentity, isPending: isCurrentBPWithIdentityPending } =
    useCurrentBusinessPartnerWithIdentityInfo();

  const { mutateAsync: updateCurrentBusinessPartner, isPending: isBpUpdatePending } =
    useUpdateCurrentBusinessPartnerPM();

  const { data: currentBusinessPartnerChange } = useCurrentBusinessPartnerChangePM();

  const { data: currentChangeOwnershipRequest } = useCurrentChangeOwnershipRequest();

  const { mutateAsync: cancelChangeOwnershipRequest, isPending: isCancelingOwnershipRequest } =
    useCancelChangeOwnershipRequest();

  const { mutateAsync: markAsCorrect } = useMarkMigrationDataAsCorrect();

  const handleMarkAsCorrect = async () => {
    try {
      await markAsCorrect();
    } catch {
      notificationService.addNotification(
        commonT('migrationCallout.markAsCorrectError'),
        '',
        Statuses.error
      );
    }
  };

  const isDivision = useIsDivision();

  const initialValues = useMemo(
    () => getFormDataFromBP(currentBusinessPartner, currentBusinessPartnerChange ?? undefined),
    [currentBusinessPartner, currentBusinessPartnerChange]
  );

  const [formFields, setFormFields] = useState(initialValues);
  const [isEditing, setIsEditing] = useState(false);
  const [isTransferOwnerModalOpen, setIsTransferOwnerModalOpen] = useState(false);
  const formData = { name: formFields.name, ...formFields.companyAddress };

  const onCancelEdit = () => {
    setIsEditing(false);
    setFormFields(initialValues);
  };

  const handleSubmit = async () => {
    // TODO: issue with GET "/api/ciam/pm/v1/businessPartner/current/change" not conforming to schema
    // Receive addressLineTwo as null, but can't save it as null. Only undefined or a string with at least 1 character.
    const trimmedAddressLineTwo = formFields.companyAddress.addressLineTwo?.trim();
    const payload = {
      ...formFields,
      name: formFields.name.trim(),
      companyAddress: {
        ...formFields.companyAddress,
        addressLineOne: formFields.companyAddress.addressLineOne.trim(),
        addressLineTwo: trimmedAddressLineTwo?.length ? trimmedAddressLineTwo : undefined,
        city: formFields.companyAddress.city.trim(),
        stateProvinceRegion: formFields.companyAddress.stateProvinceRegion.trim(),
        zipPostalCode: formFields.companyAddress.zipPostalCode.trim(),
      },
    } satisfies BusinessPartnerChangeBody;
    await updateCurrentBusinessPartner(payload, {
      onSuccess: () => {
        refetchCurrentBusinessPartner();
        invalidateCurrentBusinessPartnerChangePM();
        setIsEditing(false);
      },
      onError: (error: unknown) => {
        if (showError(error)) {
          return;
        }
        notificationService.addNotification(
          t('generalInformation.updateFailure'),
          t('generalInformation.updateFailureDesc'),
          Statuses.error
        );
      },
    });
  };

  const handleCancelTransferOwnership = async () => {
    try {
      await cancelChangeOwnershipRequest();
    } catch (error: unknown) {
      if (showError(error)) {
        return;
      }
      notificationService.addNotification(
        t('generalInformation.transferOwner.cancelFailureTitle'),
        t('generalInformation.transferOwner.cancelFailureDescription'),
        Statuses.error
      );
    }
  };

  useEffect(() => {
    setFormFields(initialValues);
  }, [initialValues]);

  const isBpPendingChange = currentBusinessPartnerChange != null;

  const isFirstPollRef = useRef(true);

  useEffect(() => {
    if (!isBpPendingChange) {
      isFirstPollRef.current = true;
      return;
    }

    const delay = isFirstPollRef.current ? 1000 : 5000;
    const timer = setTimeout(() => {
      isFirstPollRef.current = false;
      invalidateCurrentBusinessPartnerChangePM();
    }, delay);

    return () => clearTimeout(timer);
  }, [isBpPendingChange, currentBusinessPartnerChange]);

  const bpExternalId = currentBusinessPartner?.extId;
  const topconCustomerNumber = bpExternalId ? extractTopconCustomerNumber(bpExternalId) : undefined;
  const formattedCustomerNumber = topconCustomerNumber
    ? formatOrganizationNumber(topconCustomerNumber)
    : undefined;
  const handleCopyCustomerNumber = async () => {
    if (topconCustomerNumber) {
      const success = await copyToClipboard(topconCustomerNumber);
      if (success) {
        notificationService.addNotification(
          t('generalInformation.customerNumberCopied'),
          '',
          Statuses.success
        );
      }
    }
  };

  return (
    <TeContainer column grow gap="xs" padding="xs" overflow="auto" alignItems="center">
      <Header text={t('generalInformation.title')}>
        {formattedCustomerNumber && (
          <CustomerNumberContainer fill gap="s" alignItems="center">
            <TeContainer gap="xs" padding={0}>
              <TeText size={14} color="text.primary" weight={500}>
                {t('generalInformation.customerNumber')}
              </TeText>
              <TeIcon name="info" size="m" color="icon.primary" />
            </TeContainer>
            <TeContainer
              gap={'xs'}
              padding="xs"
              backgroundColor="background.tertiary"
              borderRadius="l"
              onClick={handleCopyCustomerNumber}
              style={{ cursor: 'pointer' }}
            >
              <TeText size={14} color="text.primary" weight={700}>
                {formattedCustomerNumber}
              </TeText>
              <TeIcon name="content_copy" size="m" color="icon.primary" />
            </TeContainer>
          </CustomerNumberContainer>
        )}
      </Header>

      {featureFlags.MIGRATION && featureFlags.MIGRATION_DATA_REVIEW && (
        <Section>
          <MigrationCallout
            descriptionText={commonT('migrationCallout.reviewDescription')}
            buttonLabel={commonT('migrationCallout.markAsCorrect')}
            onButtonClicked={handleMarkAsCorrect}
          />
        </Section>
      )}

      {isCurrentBusinessPartnerPending ? (
        <Loader />
      ) : (
        <Section $background="transparent">
          <TeContainerSlim fill gap="xs" column>
            <LogoDetailsContainer fill gap="xs">
              <GeneralInformationLogoSection />
              <DetailsSectionContainer
                grow
                column
                backgroundColor="background.primary"
                borderRadius="m"
              >
                <TeContainer height="xl2" alignItems="center">
                  <TeText size="l" weight="bold" color="text.secondary">
                    {t('generalInformation.details')}
                  </TeText>
                </TeContainer>
                <TeDivider dividerColor="border.secondary" />
                <SchemaValidatedForm
                  name="businessPartnerForm"
                  tesubmit={() => handleSubmit()}
                  schema={FormSchema}
                  formData={formData}
                  setFormFields={({ name, ...companyAddress }) =>
                    setFormFields({ name, companyAddress })
                  }
                  readonly={!isEditing || isBpUpdatePending}
                  touchAllOnReadonlyDisabled
                  resetFieldsStatus={!isEditing}
                >
                  {({ renderField, isValid }) => (
                    <>
                      <TeContainer column grow>
                        {isBpPendingChange ? (
                          <TeCallout
                            status="warning"
                            icon="info"
                            text={t('generalInformation.pendingChangesCallout')}
                            grow
                          />
                        ) : null}
                        <TeContainerSlim column grow gap="m">
                          <TeContainerSlim column grow gap="xs">
                            <TeLabel text={t('country', { ns: 'common' })} />
                            <CountryDropdown
                              selectedCountry={formFields.companyAddress.country}
                              readonly={!!(isBpPendingChange || !isEditing)}
                              onSelectCountry={(country) =>
                                setFormFields({
                                  ...formFields,
                                  companyAddress: {
                                    ...formFields.companyAddress,
                                    country: country?.code ?? DEFAULT_COUNTRY.code,
                                  },
                                })
                              }
                            />
                          </TeContainerSlim>
                          <TeContainerSlim column grow gap="xs">
                            <TeLabel text={t('generalInformation.companyName')} />
                            {renderField('name', t('namePlaceholder', { ns: 'common' }))}
                          </TeContainerSlim>
                          <TeContainerSlim column grow gap="xs">
                            <TeLabel text={t('officeAddress', { ns: 'common' })} />
                            {renderField(
                              'addressLineOne',
                              t('officeAddressPlaceholder', { ns: 'common' })
                            )}
                            {renderField(
                              'addressLineTwo',
                              t('officeAddress2Placeholder', { ns: 'common' })
                            )}
                          </TeContainerSlim>
                          <TeContainerSlim fill gap="s">
                            {renderField(
                              'zipPostalCode',
                              t('postalCodePlaceholder', { ns: 'common' }),
                              t('postalCode', { ns: 'common' })
                            )}
                            {renderField(
                              'city',
                              t('cityPlaceholder', { ns: 'common' }),
                              t('city', { ns: 'common' })
                            )}
                          </TeContainerSlim>
                          {renderField(
                            'stateProvinceRegion',
                            t('regionPlaceholder', { ns: 'common' }),
                            t('region', { ns: 'common' })
                          )}
                        </TeContainerSlim>
                      </TeContainer>
                      <TeDivider dividerColor="border.secondary" />

                      <TeContainer fill justifyContent="flex-end" gap="s">
                        {!isEditing && (
                          <TeButton
                            text={t('generalInformation.editDetails')}
                            size="s"
                            teclick={() => setIsEditing(true)}
                            disabled={isBpPendingChange}
                          />
                        )}
                        {isEditing && (
                          <>
                            <TeButton
                              text={t('cancel')}
                              size="s"
                              variant="neutral"
                              teclick={onCancelEdit}
                              disabled={isBpUpdatePending}
                            />
                            <TeSubmit
                              formName="businessPartnerForm"
                              text={t('saveChanges')}
                              disabled={isBpUpdatePending || !isValid}
                              size="s"
                            />
                          </>
                        )}
                      </TeContainer>
                    </>
                  )}
                </SchemaValidatedForm>
              </DetailsSectionContainer>
            </LogoDetailsContainer>
            {!isDivision && (
              <TeContainerSlim grow backgroundColor="background.primary">
                <TransferOwnershipContainer columns={2}>
                  <TeGridItem>
                    <TeContainerSlim grow column gap="xs">
                      <TeText size="l" weight="bold" color="text.primary">
                        {t('generalInformation.transferOwner.title')}
                      </TeText>
                      <TeText size="m" weight="regular" color="text.primary">
                        {currentChangeOwnershipRequest
                          ? t('generalInformation.transferOwner.ownershipInvitationSent')
                          : t('generalInformation.transferOwner.description')}
                      </TeText>
                    </TeContainerSlim>
                  </TeGridItem>
                  <TeGridItem style={{ height: '100%' }}>
                    <TeContainerSlim grow justifyContent="flex-end" alignItems="center">
                      {currentChangeOwnershipRequest ? (
                        <TeButtonOutline
                          text={t('generalInformation.transferOwner.cancelTransferButton')}
                          variant="primary"
                          size="s"
                          teclick={handleCancelTransferOwnership}
                          loading={isCancelingOwnershipRequest}
                          disabled={isCancelingOwnershipRequest}
                        />
                      ) : (
                        <TeButtonOutline
                          text={t('generalInformation.transferOwner.transferButton')}
                          variant="primary"
                          size="s"
                          teclick={() => setIsTransferOwnerModalOpen(true)}
                          loading={isCurrentBPWithIdentityPending}
                          disabled={
                            isCurrentBPWithIdentityPending || !currentBPWithIdentity?.isOwner
                          }
                        />
                      )}
                    </TeContainerSlim>
                  </TeGridItem>
                </TransferOwnershipContainer>
                <TeContainerSlim grow backgroundColor="transparent" borderRadius="m" />
              </TeContainerSlim>
            )}
          </TeContainerSlim>
        </Section>
      )}

      {isTransferOwnerModalOpen && (
        <TransferOwnerModal open={true} onClose={() => setIsTransferOwnerModalOpen(false)} />
      )}
    </TeContainer>
  );
};

export default GeneralInformation;

interface Logo {
  url: string;
  name: string;
}

export function GeneralInformationLogoSection() {
  const { t } = useTranslation('settings');
  const notificationService = useNotification();
  const [fileBeingDragged, setFileBeingDragged] = useState(false);
  const [logo, setLogo] = useState<Logo>();

  const {
    data: currentBusinessPartner,
    isPending: isCurrentBusinessPartnerPending,
    refetch: updateLogoDisplayAcrossApp,
  } = useCurrentBusinessPartner();

  const [currentBpLogo, setCurrentBpLogo] = useState<{ url: string; name: string }>();

  const { mutateAsync: updateBusinessPartnerIcon, isPending: uploadPending } =
    useUpdateBusinessPartnerIcon({
      onSuccess: () => {
        setLogo(undefined);
        updateLogoDisplayAcrossApp();
      },
      onError: () => {
        notificationService.addNotification(
          t('notifications.logoUpdatedError'),
          t('notifications.logoUpdatedErrorDescription'),
          'error'
        );
      },
    });

  const { mutate: deleteBusinessPartnerIcon, isPending: deletePending } =
    useDeleteBusinessPartnerIcon({
      onSuccess: () => {
        setLogo(undefined);
        setCurrentBpLogo(undefined);
        updateLogoDisplayAcrossApp();
      },
      onError: () => {
        notificationService.addNotification(
          t('notifications.logoRemovedError'),
          t('notifications.logoRemovedErrorDescription'),
          'error'
        );
      },
    });

  async function handleLogoSubmit(blob: Blob) {
    await updateBusinessPartnerIcon(blob);
    const randomFilename = generateRandomId(10);
    const file = new File([blob], randomFilename);
    const fileReader = new FileReader();

    fileReader.onload = function (event) {
      const dataUrl = event?.target?.result?.toString();
      if (dataUrl) {
        setCurrentBpLogo({ url: dataUrl, name: file.name });
      }
    };
    fileReader.readAsDataURL(file);
  }

  const dragStart = () => setFileBeingDragged(true);

  const dragEnd = () => setFileBeingDragged(false);

  const MAX_FILE_SIZE_MB = 5;
  const KB = 1024;

  const onUploadFile = async (file: File) => {
    if (file.size > MAX_FILE_SIZE_MB * KB * KB) {
      notificationService.addNotification(
        t('notifications.fileTooLarge', { size: (file.size / (1024 * 1024)).toFixed(2) }),
        t('notifications.fileTooLargeDescription'),
        'error'
      );
      return;
    }
    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      notificationService.addNotification(
        t('notifications.invalidFileType', { fileType: file.type }),
        t('notifications.invalidFileTypeDescription'),
        'error'
      );
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event?.target?.result?.toString();
      if (dataUrl) {
        setLogo({ url: dataUrl, name: file.name });
      } else {
        notificationService.addNotification(
          t('notifications.invalidFile'),
          t('notifications.invalidFileDescription'),
          'error'
        );
      }
    };
    reader.onerror = () => {
      notificationService.addNotification(
        t('notifications.fileReadError'),
        t('notifications.fileReadErrorDescription'),
        'error'
      );
    };
    reader.readAsDataURL(file);
  };
  const fileDrop = (files: File[]) => {
    dragEnd();
    if (files.length > 1) {
      notificationService.addNotification(
        t('notifications.multipleFiles'),
        t('notifications.multipleFilesDescription'),
        'error'
      );
      return;
    }
    const [file] = files;
    if (!file) return;
    onUploadFile(file);
  };

  const onUploadClick = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;
    if (event.target.files.length > 1) {
      notificationService.addNotification(
        t('notifications.multipleFiles'),
        t('notifications.multipleFilesDescription'),
        'error'
      );
      return;
    }
    const [file] = event.target.files;
    if (!file) return;
    onUploadFile(file);
  };

  const onRemoveClick = () => {
    deleteBusinessPartnerIcon();
  };

  // PAP keeps returning the same presigned iconURL after a delete (it then
  // serves the default image), so only a new URL replaces what is shown: a
  // refetch with the same URL must not bring back the deleted logo.
  const serverIconURL = currentBusinessPartner?.iconURL;
  useEffect(() => {
    if (serverIconURL) {
      setCurrentBpLogo({ url: serverIconURL, name: generateRandomId(10) });
    }
  }, [serverIconURL]);

  return (
    <>
      <LogoSectionContainer
        width={400}
        column
        grow
        backgroundColor="background.primary"
        borderRadius={4}
      >
        <TeContainer height="xl2" alignItems="center">
          <TeText size={16} weight={700} color="text.secondary">
            {t('generalInformation.logo')}
          </TeText>
        </TeContainer>

        <TeDivider dividerColor="border.secondary" />

        <TeContainer column grow alignItems="center">
          <CompanyIcon
            size="xxl"
            loading={isCurrentBusinessPartnerPending}
            iconURL={currentBpLogo?.url}
          />

          <FileDropContainer
            filesDragStarted={dragStart}
            filesDragStopped={dragEnd}
            filesDropped={fileDrop}
            filesBeingDragged={fileBeingDragged}
          >
            <TeContainerSlim gap="xs" column alignItems="center">
              <TeIcon name="upload" size="xl" />
              <TeContainerSlim gap="xs2" column alignItems="center">
                <TeText size={16} weight={500}>
                  {t('generalInformation.uploadOrDragAndDrop')}
                </TeText>
                <TeText size={12} color="text.secondary">
                  {t('generalInformation.allowedImages')}
                </TeText>
              </TeContainerSlim>
            </TeContainerSlim>
            <TeContainerSlim gap="xs" alignItems="center">
              {currentBpLogo ? (
                <TeButton
                  size="s"
                  disabled={!!logo || isCurrentBusinessPartnerPending}
                  teclick={onRemoveClick}
                  loading={deletePending}
                  icon="delete"
                  variant="neutral"
                />
              ) : null}
              <UploadButton
                text={t('generalInformation.uploadNewImage')}
                disabled={!!logo || deletePending || isCurrentBusinessPartnerPending}
                onButtonClick={onUploadClick}
                accept={BTN_ACCEPT}
              />
            </TeContainerSlim>
          </FileDropContainer>
        </TeContainer>
      </LogoSectionContainer>
      <AdjustLogoModal
        pending={uploadPending}
        logo={logo}
        onClose={() => setLogo(undefined)}
        onSubmit={handleLogoSubmit}
      />
    </>
  );
}

function Loader() {
  const { t } = useTranslation('settings');
  return (
    <Section $padding="16px">
      <PlaceholderContainer
        borderRadius={4}
        grow
        backgroundColor="background.secondary"
        hasLoader
        text={t('loading')}
      />
    </Section>
  );
}

//specific breakpoint to handle very small screens in this component
const CustomerNumberContainer = styled(TeContainerSlim)`
  @media (max-width: 380px) {
    flex-direction: column;
    align-items: flex-start !important;
    justify-content: flex-start !important;
  }
`;

const LogoDetailsContainer = styled(TeContainerSlim)`
  @media (max-width: ${BREAKPOINTS.mobile}) {
    flex-direction: column;
    align-items: flex-start !important;
    justify-content: flex-start !important;
    width: 100% !important;
  }
`;

const LogoSectionContainer = styled(TeContainerSlim)`
  @media (max-width: ${BREAKPOINTS.mobile}) {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 100% !important;
  }
`;

const DetailsSectionContainer = styled(TeContainerSlim)`
  @media (max-width: ${BREAKPOINTS.mobile}) {
    width: 100% !important;
  }
`;

const TransferOwnershipContainer = styled(TeGrid)`
  grid-template-columns: repeat(2, minmax(0px, 1fr)) !important;
  @media (max-width: ${BREAKPOINTS.mobile}) {
    font-size: 14px !important;
  }
`;
